<script setup>
import Avatar from 'next/avatar/Avatar.vue';
import { ref, computed, watch, nextTick } from 'vue';
import { useKeyboardNavigableList } from 'dashboard/composables/useKeyboardNavigableList';

const props = defineProps({
  searchKey: {
    type: String,
    default: '',
  },
  participants: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['selectMember']);

const tagGroupRef = ref(null);
const selectedIndex = ref(0);

const items = computed(() => {
  const search = props.searchKey?.trim().toLowerCase() || '';
  if (!search) return props.participants;
  return props.participants.filter(
    p =>
      p.name.toLowerCase().includes(search) ||
      p.number.includes(search)
  );
});

const adjustScroll = () => {
  nextTick(() => {
    if (tagGroupRef.value) {
      const el = tagGroupRef.value.querySelector(
        `#group-member-${selectedIndex.value}`
      );
      if (el) el.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }
  });
};

const onSelect = () => {
  const member = items.value[selectedIndex.value];
  if (member) emit('selectMember', member);
};

useKeyboardNavigableList({
  items,
  onSelect,
  adjustScroll,
  selectedIndex,
});

watch(items, newList => {
  if (newList.length < selectedIndex.value + 1) {
    selectedIndex.value = 0;
  }
});

const onHover = index => {
  selectedIndex.value = index;
};

const onMemberSelect = index => {
  selectedIndex.value = index;
  onSelect();
};
</script>

<template>
  <div>
    <ul
      v-if="items.length"
      ref="tagGroupRef"
      class="vertical dropdown menu mention--box bg-n-solid-1 p-1 rounded-xl text-sm overflow-auto absolute w-full z-20 shadow-md left-0 leading-[1.2] bottom-full max-h-[12.5rem] border border-solid border-n-strong"
      role="listbox"
    >
      <li class="px-2 py-1.5 text-xs font-medium tracking-wide text-n-slate-11">
        Membros do grupo
      </li>
      <li
        v-for="(member, index) in items"
        :id="`group-member-${index}`"
        :key="member.number"
      >
        <div
          :class="{ 'bg-n-alpha-black2': index === selectedIndex }"
          class="flex items-center px-2 py-1 rounded-md cursor-pointer"
          role="option"
          @click="onMemberSelect(index)"
          @mouseover="onHover(index)"
        >
          <div class="ltr:mr-2 rtl:ml-2">
            <Avatar :name="member.name" src="" rounded-full />
          </div>
          <div class="overflow-hidden flex-1 max-w-full whitespace-nowrap text-ellipsis">
            <div class="flex items-center gap-1.5">
              <h5
                class="overflow-hidden mb-0 text-sm whitespace-nowrap text-n-slate-11 text-ellipsis"
                :class="{ 'text-n-slate-12': index === selectedIndex }"
              >
                {{ member.name }}
              </h5>
              <span
                v-if="member.is_admin"
                class="shrink-0 text-[10px] font-semibold px-1 py-px rounded bg-n-brand/15 text-n-brand"
              >
                Admin
              </span>
            </div>
            <div
              class="overflow-hidden text-xs whitespace-nowrap text-ellipsis text-n-slate-10"
              :class="{ 'text-n-slate-11': index === selectedIndex }"
            >
              +{{ member.number }}
            </div>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>
